import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Search, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import Avatar from "./Avatar";
import ThemeToggle from "../ThemeToggle";

const MENUS = [
  {
    label: "Promotion",
    key: "promotion",
    items: [
      { label: "All Promotions", to: "/marketplace" },
      { label: "TikTok Promotion", to: "/marketplace?platform=tiktok" },
      { label: "Instagram Promotion", to: "/marketplace?platform=meta" },
      { label: "Spotify Playlists", to: "/marketplace?platform=spotify" },
    ],
  },
  {
    label: "Visuals",
    key: "visuals",
    items: [
      { label: "All Visual Services", to: "/video-marketplace" },
      { label: "Music Videos", to: "/video-marketplace?type=music_video" },
      { label: "Lyric Videos", to: "/video-marketplace?type=lyric_video" },
      { label: "Visualizers", to: "/video-marketplace?type=visualizer" },
      { label: "Cover Art", to: "/video-marketplace?type=album_artwork" },
    ],
  },
];

// Distribution is visible to everyone (including guests).
const buildDiscover = (user) => {
  const items = [
    { label: "Distribution", to: "/distribution" },
    { label: "Distribution Plans", to: "/distribution-plans" },
    { label: "DopeMedia Playlists", to: "/playlists" },
    { label: "Trusted Partners", to: "/partners" },
  ];

  return { label: "Discover", key: "discover", items };
};

const NavDropdown = ({ menu, onNavigate, isActive }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const enter = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  };
  const leave = () => {
    timer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button
        type="button"
        className={`inline-flex items-center gap-1.5 text-sm font-medium py-2 transition-colors ${
          open ? "text-[#CCFF00]" : "text-zinc-300 hover:text-white"
        }`}
        data-testid={`nav-menu-${menu.key}`}
        onClick={() => onNavigate(menu.items[0].to)}
      >
        {menu.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-3 w-60" data-testid={`nav-dropdown-${menu.key}`}>
          <div className="rounded-xl border border-white/10 bg-[#121212] shadow-[0_16px_48px_rgba(0,0,0,0.6)] overflow-hidden py-2">
            {menu.items.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => onNavigate(item.to)}
                className={`w-full flex items-center justify-between gap-2 text-left px-5 py-2.5 text-sm font-medium transition-colors hover:bg-[#1C1C1C] ${
                  isActive(item.to) ? "text-[#CCFF00]" : "text-zinc-300 hover:text-white"
                }`}
                data-testid={`nav-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {item.label}
                {item.badge && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#CCFF00]/15 text-[#CCFF00]">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarTimer = useRef(null);

  const isActive = (path) => location.pathname + location.search === path || location.pathname === path;

  useEffect(() => {
    setOpen(false);
    setAvatarOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const go = (to) => navigate(to);

  const menus = [...MENUS, buildDiscover(user)];

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/marketplace?search=${encodeURIComponent(q)}` : "/marketplace");
    setOpen(false);
  };

  const avatarEnter = () => {
    if (avatarTimer.current) clearTimeout(avatarTimer.current);
    setAvatarOpen(true);
  };
  const avatarLeave = () => {
    avatarTimer.current = setTimeout(() => setAvatarOpen(false), 150);
  };

  return (
    <>
      <nav className="navbar justify-between gap-4" data-testid="navbar">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <span className="font-['Syne'] text-xl sm:text-2xl font-extrabold tracking-tighter text-white">
              DopeMedia
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {menus.map((menu) => (
              <NavDropdown key={menu.key} menu={menu} onNavigate={go} isActive={isActive} />
            ))}
            {user && (
              <Link
                to="/dashboard"
                className={`text-sm font-medium py-2 transition-colors ${
                  isActive("/dashboard") ? "text-[#CCFF00]" : "text-zinc-300 hover:text-white"
                }`}
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
            )}
          </div>
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <ThemeToggle inline />
          <form onSubmit={submitSearch} className="hidden md:flex items-center relative" data-testid="nav-search-form">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search gigs…"
              className="w-40 lg:w-52 bg-[#141414] border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#CCFF00]/60"
              data-testid="nav-search-input"
            />
          </form>

          {user ? (
            <div className="hidden lg:block relative" onMouseEnter={avatarEnter} onMouseLeave={avatarLeave}>
              <button
                type="button"
                className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[#1A1A1A] transition-colors"
                data-testid="nav-avatar-menu"
              >
                <Avatar src={user.profile_picture} name={user.name} size="sm" />
                <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${avatarOpen ? "rotate-180" : ""}`} />
              </button>
              {avatarOpen && (
                <div className="absolute right-0 top-full pt-2 w-56" data-testid="avatar-dropdown">
                  <div className="rounded-xl border border-white/10 bg-[#121212] shadow-[0_16px_48px_rgba(0,0,0,0.6)] overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                      <p className="text-xs text-zinc-500 capitalize">{user.role}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => go("/dashboard")}
                      className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-[#1C1C1C] hover:text-white transition-colors"
                      data-testid="avatar-menu-dashboard"
                    >
                      Dashboard
                    </button>
                    <button
                      type="button"
                      onClick={() => { logout(); navigate("/"); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-t border-white/10"
                      data-testid="avatar-menu-logout"
                    >
                      <LogOut className="w-4 h-4" /> Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/login"
                className="text-sm font-medium text-zinc-300 hover:text-white transition-colors"
                data-testid="nav-login"
              >
                Login
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="btn-primary text-sm px-6 py-2 rounded-full">Get Started</Button>
              </Link>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-white relative z-[101]"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-[#0A0A0A] flex flex-col overflow-y-auto"
          style={{ zIndex: 100, paddingTop: "var(--navbar-height, 64px)" }}
          data-testid="mobile-menu"
        >
          <div className="flex flex-col p-6 gap-5">
            <form onSubmit={submitSearch} className="relative">
              <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search gigs…"
                className="w-full bg-[#141414] border border-white/10 rounded-full pl-11 pr-4 py-3 text-base text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#CCFF00]/60"
                data-testid="mobile-search-input"
              />
            </form>

            {menus.map((menu) => (
              <div key={menu.key}>
                <div className="font-['Syne'] text-lg font-bold text-white mb-2">{menu.label}</div>
                <div className="flex flex-col">
                  {menu.items.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => go(item.to)}
                      className="flex items-center gap-2 text-left px-2 py-2.5 text-zinc-400 hover:text-white transition-colors"
                      data-testid={`mobile-item-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {item.label}
                      {item.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#CCFF00]/15 text-[#CCFF00]">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              {user ? (
                <>
                  <Link to="/dashboard" data-testid="mobile-nav-dashboard">
                    <Button className="btn-primary w-full py-3 text-base rounded-full">Dashboard</Button>
                  </Link>
                  <button
                    type="button"
                    onClick={() => { logout(); navigate("/"); }}
                    className="w-full py-3 text-base text-red-400 font-medium"
                    data-testid="mobile-nav-logout"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" data-testid="mobile-nav-login">
                    <Button
                      variant="outline"
                      className="w-full py-3 text-base bg-transparent border-[#2A2A2A] text-white hover:bg-[#1A1A1A] rounded-full"
                    >
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" data-testid="mobile-nav-register">
                    <Button className="btn-primary w-full py-3 text-base rounded-full">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
